import { useState, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, normalizePhone, formatSpanishDate, formatTime } from "@/lib/api";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, Calendar as CalIcon, Check, X, Upload, MessageCircle, RotateCw, RefreshCcw, User, Sparkles, Send, Sun, Moon } from "lucide-react";
import UrgencyCard from "@/components/UrgencyCard";
import { WhatsAppQRModal } from "@/components/ChatBot";

// Ronda 32.14: variantes cálidas del mensaje de fallback («no te he
// entendido»). Se rotan para que, si la clienta escribe dos veces
// seguidas algo que el bot no entiende, no reciba palabra por palabra
// el mismo texto. Todas dicen la misma información (opciones sí/no/
// antes/después/fecha concreta + Sol abajo a la derecha) pero con
// redacciones distintas.
const _FALLBACK_VARIANTS = [
  "Ay, no acabo de pillarte. Si me dices «sí», «no me va bien», «un poco antes» o «un poco después», o un día concreto («el 22» o «el 22 de agosto»), lo cuadramos enseguida. Para cualquier otra cosa (tipos de consulta, precio, cómo trabaja Bea…) tienes a Sol abajo a la derecha. ✨",
  "Perdona, esta vez no te he cogido la idea. Puedes decirme «sí» o «no», si prefieres «un pelín antes» o «un pelín después», o simplemente un día concreto («el 22», «el 22 de agosto») y sigo desde ahí. Y si querías preguntar algo distinto, Sol te lo cuenta genial abajo a la derecha.",
  "Uy, se me ha escapado a qué te referías. Si me lo pones cortito («sí», «no», «antes», «después» o un día concreto tipo «el 22 de agosto») lo tenemos en un momento. Para preguntas de otro tipo, Sol está esperándote en la esquina inferior derecha.",
  "Vaya, esta vez no te sigo del todo. Con un «sí me viene bien», un «no», un «un poco antes/después» o un día concreto («el 22», «el 22 de agosto») avanzo rápido. Y para todo lo que no sea cuadrar la cita, ahí abajo tienes a Sol.",
  "No te termino de entender lo que me pides. Prueba con «sí», «no me va bien», «antes» o «después», o dime directamente un día («el 22» o «el 22 de agosto»). Si querías preguntar otra cosa, Sol está abajo a la derecha para ayudarte.",
];

const _nextFallbackVariant = (idxRef) => {
  const v = _FALLBACK_VARIANTS[idxRef.current % _FALLBACK_VARIANTS.length];
  idxRef.current += 1;
  return v;
};

export default function Booking() {
  const nav = useNavigate();
  const [step, setStep] = useState("phone");
  // Ronda 33.4 · reschedule mode: "new" (reserva desde cero) | "reschedule"
  // (cambio de horario de cita existente, hereda el pago).
  const [mode, setMode] = useState("new");
  const [oldAppointment, setOldAppointment] = useState(null);
  const [rawPhone, setRawPhone] = useState("");
  const [phone, setPhone] = useState("");
  const [name, setName] = useState("");
  // Ronda 32.16: si el teléfono coincide con una reserva previa,
  // guardamos el nombre para saludar a la clienta reconociéndola.
  const [returningClient, setReturningClient] = useState(null);
  const [clientEmail, setClientEmail] = useState("");
  const [config, setConfig] = useState(null);
  const [appointment, setAppointment] = useState(null);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [booking, setBooking] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showCancel, setShowCancel] = useState(false);
  const [cancelReason, setCancelReason] = useState("");
  const [showQR, setShowQR] = useState(false);

  // Conversation state
  // stages: intro | ask_band | proposing | disambiguate_shift | exhausted
  const [convoStage, setConvoStage] = useState("intro");
  const [chatLog, setChatLog] = useState([]);
  const [excluded, setExcluded] = useState([]);
  const [band, setBand] = useState(null); // 'morning' | 'afternoon' — locked
  const [currentSlot, setCurrentSlot] = useState(null);
  const [pendingShift, setPendingShift] = useState(null); // 'earlier' | 'later'
  const [chatText, setChatText] = useState("");
  const [rejectCount, setRejectCount] = useState(0);
  const chatEndRef = useRef(null);
  // Ronda 32.14: rotación de mensajes fallback para no sonar a plantilla
  // cuando se repite en la misma conversación. useRef para no forzar
  // re-renders al incrementar.
  const fallbackIdxRef = useRef(0);
  const missRef = useRef(0);
  const pendingAskRef = useRef(null);
  const stageBeforeRef = useRef(null);
  const escalateRequestRef = useRef("");
  const [escalateReturnStage, setEscalateReturnStage] = useState(null);
  // Ronda 31.2: session_id estable para conversar con Sol tras el pago.
  // Se conserva en sessionStorage para persistir entre refresh de la misma
  // pestaña pero no bleedear a otras pestañas / dispositivos.
  const sessionId = useRef(
    (typeof window !== "undefined" && window.sessionStorage.getItem("bea_booking_session"))
    || (() => {
      const sid = `booking-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
      if (typeof window !== "undefined") window.sessionStorage.setItem("bea_booking_session", sid);
      return sid;
    })()
  );

  const [uploadFromExisting, setUploadFromExisting] = useState(false);

  // Ronda 26: lista de espera en modo vacaciones
  const [waitlistName, setWaitlistName] = useState("");
  const [waitlistPhone, setWaitlistPhone] = useState("");
  const [waitlistEmail, setWaitlistEmail] = useState("");
  const [waitlistSubmitted, setWaitlistSubmitted] = useState(false);
  const [waitlistLoading, setWaitlistLoading] = useState(false);

  const submitWaitlist = async () => {
    // Ronda 32: aceptamos teléfonos internacionales. La validación real
    // la hace el backend (`phone_intl.normalize_intl_phone`). Aquí solo
    // comprobamos que hay algún dígito para no mandar un submit vacío.
    const hasDigits = /\d/.test(waitlistPhone);
    if (!waitlistName.trim() || waitlistName.trim().length < 2) {
      toast.error("Introduce tu nombre.");
      return;
    }
    if (!hasDigits) {
      toast.error("Introduce un número de teléfono válido.");
      return;
    }
    // Ronda 32: el email ya NO es obligatorio. Solo lo validamos si la
    // clienta lo ha rellenado — así puede apuntarse solo con su teléfono.
    const emailTrim = waitlistEmail.trim();
    if (emailTrim && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailTrim)) {
      toast.error("Ese email no me parece válido. Déjalo en blanco si no quieres darlo.");
      return;
    }
    setWaitlistLoading(true);
    try {
      await api.post("/waitlist/join", {
        phone: waitlistPhone.trim(),
        name: waitlistName.trim(),
        email: emailTrim || undefined,
      });
      setWaitlistSubmitted(true);
      toast.success("Apuntada a la lista de espera. Te avisaremos cuando Bea vuelva.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No he podido apuntarte. Prueba de nuevo en unos segundos.");
    }
    setWaitlistLoading(false);
  };

  useEffect(() => {
    api.get("/config/public").then((r) => setConfig(r.data));
  }, []);

  useEffect(() => {
    if (chatEndRef.current) chatEndRef.current.scrollIntoView({ behavior: "smooth" });
  }, [chatLog, convoStage]);

  // Ronda 31.7: id monotónico para claves estables en la lista del chat.
  const pushBot = (text, meta = null) => setChatLog((l) => [...l, { id: `m-${Date.now()}-${l.length}-b`, role: "bot", text, meta, ts: new Date().toISOString() }]);
  const pushUser = (text) => setChatLog((l) => [...l, { id: `m-${Date.now()}-${l.length}-u`, role: "user", text, ts: new Date().toISOString() }]);

  // Historial completo del chat de reservas para el panel de Bea.
  useEffect(() => {
    if (!chatLog.length) { return undefined; }
    const t = setTimeout(() => {
      api.post("/booking/chat-transcript", {
        session_id: sessionId.current,
        phone: phone || undefined,
        name: name || undefined,
        messages: chatLog.map((m) => ({ role: m.role, text: m.text, ts: m.ts })),
      }).catch(() => {});
    }, 1200);
    return () => clearTimeout(t);
  }, [chatLog, phone, name]);

  const newSession = () => {
    const sid = `booking-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    window.sessionStorage.setItem("bea_booking_session", sid);
    sessionId.current = sid;
  };

  const calendarErrorText = (e, fallback) => (
    e?.response?.status === 503
      ? `${e?.response?.data?.detail || "No he podido consultar la agenda ahora mismo."} Mientras tanto no puedo reservar ningún hueco.`
      : fallback
  );

  const submitPhone = async () => {
    const n = normalizePhone(rawPhone);
    if (!n) {
      toast.error("Introduce un móvil español de 9 dígitos (con o sin +34).");
      return;
    }
    setPhone(n);
    setLoading(true);
    try {
      const { data } = await api.post("/booking/check-phone", { phone: n });
      if (data.calendar_offline) {
        toast.error("No he podido consultar la agenda ahora mismo. Para no arriesgar una reserva doble, inténtalo de nuevo en unos minutos.");
        setLoading(false);
        return;
      }
      if (data.appointment) {
        setAppointment(data.appointment);
        setStep("existing");
      } else {
        // Ronda 32.16: reconocer clienta recurrente sin cita futura.
        // Prerellenamos el nombre y guardamos flag para saludo cálido.
        if (data.known_client && data.known_client.name) {
          setName(data.known_client.name);
          setReturningClient(data.known_client);
        } else {
          setReturningClient(null);
        }
        setStep("conversation");
        setConvoStage("intro");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No pudimos comprobar tu teléfono. Intenta de nuevo en unos segundos.");
    }
    setLoading(false);
  };

  const startConversation = () => {
    if (!name.trim() || name.trim().length < 2) {
      toast.error("Necesito tu nombre para la reserva.");
      return;
    }
    setChatLog([
      { id: `intro-1-${Date.now()}`, role: "bot", text: `Encantada, ${name.trim()}. Antes de mirar la agenda, dime una cosa:` },
      { id: `intro-2-${Date.now()}`, role: "bot", text: "¿Qué te viene mejor, por la mañana o por la tarde? Me adapto a lo que más te cuadre.", meta: { type: "band_question" } },
    ]);
    setConvoStage("ask_band");
  };

  const pickBand = async (b) => {
    setBand(b);
    setExcluded([]);
    setRejectCount(0);
    pushUser(b === "morning" ? "Por la mañana" : "Por la tarde");
    setConvoStage("proposing");
    await fetchProposal({ band: b, excludedList: [] });
  };

  // core proposal fetcher; band is required
  const fetchProposal = async ({ band: reqBand = band, excludedList = excluded, shift, shift_kind, anchor } = {}) => {
    if (!reqBand) return;
    setLoading(true);
    try {
      const body = { band: reqBand, excluded: excludedList };
      if (shift) body.shift = shift;
      if (shift_kind) body.shift_kind = shift_kind;
      if (anchor) body.anchor = anchor;
      const { data } = await api.post("/booking/propose-slot", body);
      if (data.reason === "exhausted" || !data.slot) {
        // Ronda 32.18: cuando la franja pedida está completa, comprobamos
        // proactivamente la OTRA franja. Si hay hueco allí, lo ofrecemos
        // en la misma respuesta — mejor UX que decir «no hay» y esperar
        // a que la clienta pregunte por la otra franja.
        const otherBand = (reqBand === "morning") ? "afternoon" : "morning";
        const bandLabel = reqBand === "morning" ? "mañana" : "tarde";
        const otherLabel = otherBand === "morning" ? "mañana" : "tarde";
        const alreadyOffered = excludedList.length > 0;
        let altSlot = null;
        try {
          const { data: altData } = await api.post("/booking/propose-slot", {
            band: otherBand,
            excluded: [],
          });
          if (altData && altData.slot) altSlot = altData.slot;
        } catch (e) { /* silencio: si falla, seguimos con el mensaje normal */ }

        if (altSlot) {
          pushBot(
            `${alreadyOffered ? `Por la ${bandLabel}, aparte de lo que ya te he propuesto, no me queda otro hueco.` : `Por la ${bandLabel} no me queda hueco.`} Pero por la ${otherLabel}, podría ser el ${formatSpanishDate(altSlot.start)} a las ${formatTime(altSlot.start)} — ¿te encaja?`,
            { type: "slot", slot: altSlot, alt_band: otherBand }
          );
          setBand(otherBand);
          setExcluded([]);
          setCurrentSlot(altSlot);
          setLoading(false);
          return;
        }
        pushBot(
          alreadyOffered
            ? "Aparte de lo que ya te he propuesto, no me quedan más huecos. Si quieres, dime cuál de esos te encaja o un día concreto y lo miro."
            : "Ahora mismo no encuentro huecos libres. Prueba con otro día concreto («el 22», por ejemplo) o escríbeme por WhatsApp y lo miramos con calma."
        );
        setCurrentSlot(null);
        setLoading(false);
        return;
      }
      // ORIGINAL BRANCH: cuando no hay slot y no es exhausted, mantenemos
      // el mensaje anterior por si viene un error concreto (shift_no_more, etc.)
      if (!data.slot) {
        // Ronda 25: si el shift no ha encontrado hueco (no_time_shift o
        // no_date_shift), NO limpiamos currentSlot — así el cliente puede
        // seguir la conversación pidiendo "una fecha anterior" o "otra hora"
        // sin quedarse sin ancla. Solo limpiamos si es una petición base
        // sin shift y no hay huecos en absoluto.
        const isShiftFailure = shift && (data.reason === "no_time_shift" || data.reason === "no_date_shift");
        // Ronda 27 P3.20: mensaje específico cuando el cliente pide una fecha
        // anterior y no queda ninguna (ya está en la primera cita libre).
        const noEarlierMsg = shift === "earlier" && data.reason === "no_date_shift"
          ? "Esta es la primera cita disponible. Si se libera algo antes, te avisaremos — si no recibes ningún aviso, da por hecho que tu cita queda confirmada tal cual. Si es urgente, puedo recomendarte nuestro gabinete de confianza para casos así."
          : null;
        pushBot(
          noEarlierMsg ? noEarlierMsg :
          shift
            ? `En ese margen concreto no me queda hueco ${reqBand === "morning" ? "por la mañana" : "por la tarde"}. Si me dices un día concreto (por ejemplo «el 22» o «el 22 de agosto») lo miro directamente en mi agenda, o dime si prefieres cambiar de franja.`
            : `No me quedan huecos ${reqBand === "morning" ? "por la mañana" : "por la tarde"} en las próximas semanas. Si quieres, dime un día concreto (por ejemplo «el 22») y miro directamente, o prueba con la otra franja diciéndome «mejor por ${reqBand === "morning" ? "la tarde" : "la mañana"}».`,
          { type: "no_more" }
        );
        // Do NOT jump to 'exhausted' — keep the door open for specific-date requests
        if (!isShiftFailure) setCurrentSlot(null);
        setLoading(false);
        return;
      }
      const s = data.slot;
      // Ronda 25: aviso sutil si el hueco propuesto está fuera de la ventana
      // preferente 7-14 días (agenda saturada en corto plazo).
      // Ronda 32.20: si el hueco está fuera de la ventana preferente, ANTES
      // de mostrarlo comprobamos proactivamente si la OTRA franja tiene
      // hueco más cercano — el fix de Ronda 32.18 solo comprobaba cuando
      // NO había slot; en el caso real de Bea el bot ofrecía el 1 sep por
      // la mañana sin mencionar que la tarde del 25 (que él mismo acababa
      // de ofrecer) seguía disponible.
      if (data.outside_preferred_window && reqBand !== "any") {
        const otherBand = (reqBand === "morning") ? "afternoon" : "morning";
        const bandLabel = reqBand === "morning" ? "mañana" : "tarde";
        const otherLabel = otherBand === "morning" ? "mañana" : "tarde";
        try {
          const { data: altData } = await api.post("/booking/propose-slot", {
            band: otherBand,
            excluded: [],
          });
          const altSlot = altData && altData.slot;
          const altCloser = altSlot && !altData.outside_preferred_window;
          if (altCloser) {
            pushBot(
              `Por la ${bandLabel} lo más cercano que tengo es el ${formatSpanishDate(s.start)} a las ${formatTime(s.start)}. Pero por la ${otherLabel}, podría ser el ${formatSpanishDate(altSlot.start)} a las ${formatTime(altSlot.start)} — ¿te encaja?`,
              { type: "slot", slot: altSlot, alt_band: otherBand }
            );
            setBand(otherBand);
            setCurrentSlot(altSlot);
            setLoading(false);
            return;
          }
        } catch (e) { /* si falla, seguimos con el mensaje normal */ }
      }
      const prefix = data.outside_preferred_window
        ? "Un poco más adelante sí tengo hueco: "
        : "";
      pushBot(`${prefix}Podría ser el ${formatSpanishDate(s.start)} a las ${formatTime(s.start)} — ¿te encaja?`, { type: "slot", slot: s });
      setCurrentSlot(s);
    } catch (e) {
      if (e?.response?.status === 503) setCurrentSlot(null);
      pushBot(calendarErrorText(e, "Ay, algo se me ha atascado consultando la agenda. Prueba de nuevo en unos segundos o, si prefieres, seguimos por WhatsApp."), { type: "calendar_error" });
    }
    setLoading(false);
  };

  // Ronda 33.1 (2026-02 CRÍTICO) — Doble confirmación estructural.
  // Antes se creaba la reserva con un único "sí" (o con cualquier
  // yes-word aceptado por el backend). Reporte real: «y si luego no
  // puedo?» generó una reserva en producción sin ningún click.
  // Ahora el flujo es OBLIGATORIAMENTE:
  //   1) acceptProposal → repite fecha/hora exactas, pide 2ª confirmación
  //   2) finalizeBooking → única función que escribe en Google Calendar
  const acceptProposal = async (slot) => {
    pushUser("Sí, me viene bien");
    setSelectedSlot(slot);
    setCurrentSlot(slot);
    setConvoStage("awaiting_final_confirmation");
    pushBot(
      `Perfecto. Antes de reservarlo, confírmame que va bien: el ${formatSpanishDate(slot.start)} a las ${formatTime(slot.start)}. ¿Lo cerramos así?`,
      { type: "final_confirmation", slot }
    );
    setLoading(false);
  };

  const cancelFinalConfirmation = () => {
    pushUser("No, prefiero otro");
    setConvoStage("proposing");
    pushBot(
      "Vale, sin problema. Dime, ¿te miro otra hora, otro día, o cambio de franja?"
    );
    setLoading(false);
  };

  const finalizeBooking = async (slot) => {
    pushUser("Sí, confírmalo");
    setSelectedSlot(slot);
    setLoading(true);
    // Ronda 33.4 — modo reschedule: hereda pago, no pide justificante.
    if (mode === "reschedule" && oldAppointment?.event_id) {
      try {
        const { data } = await api.post("/booking/reschedule", {
          phone,
          old_event_id: oldAppointment.event_id,
          new_start: slot.start,
          new_end: slot.end,
          reason: cancelReason || undefined,
        });
        pushBot(
          `Hecho. Tu cita queda movida al ${formatSpanishDate(slot.start)} a las ${formatTime(slot.start)}. El pago original se mantiene — no hace falta justificante nuevo. Aquí sigo si necesitas algo más.`,
          { type: "reschedule_done", slot }
        );
        toast.success("Cita reprogramada.");
        setBooking({ event_id: data.new_event_id, phone, name, start: slot.start, end: slot.end });
        setAppointment({ event_id: data.new_event_id, start: slot.start, end: slot.end, status: "confirmed", has_receipt: true });
        setMode("new");
        setOldAppointment(null);
        // Ronda 33.7 — tras un reschedule no hay que pedir justificante ni
        // mostrar el bloque de pago. `reschedule_done` es un estado
        // terminal: mantiene el chat abierto para Q&A pero NO pinta ni
        // payment_info ni "Subir justificante".
        setConvoStage("reschedule_done");
      } catch (e) {
        const detail = e?.response?.data?.detail || "prueba de nuevo en unos segundos";
        pushBot(`Ay, no he podido mover la cita: ${detail}. Tu cita original sigue en pie tal cual estaba.`);
        setConvoStage("proposing");
      }
      setLoading(false);
      return;
    }
    try {
      const tc = slot.time_change;
      const { data } = await api.post("/booking/create", {
        name: name.trim(),
        phone,
        start: slot.start,
        end: slot.end,
        client_email: clientEmail || undefined,
        time_change_mode: tc?.mode,
        requested_start: tc?.requested_start,
        requested_end: tc?.requested_end,
      });
      setBooking(data);
      if (tc?.mode === "ask") {
        pushBot(
          clientEmail
            ? `Recuerda: le pregunto a Bea si puede a las ${tc.requested}. Si puede, te llegará un email cambiándote la cita; si no, se queda a las ${formatTime(slot.start)}.`
            : `Recuerda: le pregunto a Bea si puede a las ${tc.requested}. Si quieres respuesta, escríbenos por WhatsApp desde el botón de la web; si no, tu cita se queda a las ${formatTime(slot.start)}.`,
          { type: "time_change_pending" }
        );
      }
      // Stay in the chat and let the client upload the receipt inline
      pushBot(
        `Perfecto, ya te tengo apuntada el ${formatSpanishDate(slot.start)} a las ${formatTime(slot.start)}. Solo nos queda el pago (${config?.consultation_price_eur || 75}€) por transferencia bancaria y que me subas el justificante aquí mismo.`,
        { type: "booking_created", booking: data, slot }
      );
      pushBot(
        "Ten la seguridad de que tu cita queda reservada en cuanto envíes el justificante. Si te surge algún imprevisto y no puedes hacer la consulta, no te preocupes: entra en «Gestionar mi cita», cuéntalo y tendrás prioridad para cambiarla. El hueco solo se libera si no llega el justificante en el plazo indicado.",
        { type: "reassurance" }
      );
      pushBot(
        config?.content_texts?.chatbot_times_notice ||
          "Un aviso sobre los tiempos: tu cita no quedará confirmada hasta confirmar el pago; y como hay bastante demanda, te agradeceríamos que subas el comprobante de la transferencia cuanto antes, ya que si se tarda más de 2 días el sistema libera el hueco para otra persona. ¡Muchas gracias!",
        { type: "payment_timing" }
      );
      // Ronda 32.13: dejar EXPLÍCITO que la clienta puede seguir escribiendo
      // tras la reserva sin que eso reabra la negociación de fecha.
      pushBot(
        "Puedes seguir escribiéndome por aquí si tienes cualquier duda sobre tu cita o el pago; no se cierra el chat. Si necesitaras cambiar el día o la hora, hazlo desde «Gestionar mi cita» para no pisar otro hueco. Y si quieres saber algo sobre las consultas o sobre Bea, pregúntale a Sol, abajo a la derecha.",
        { type: "chat_open_hint" }
      );
      setConvoStage("await_receipt");
      toast.success("Hueco reservado. Sube el justificante para confirmar.");
    } catch (e) {
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail || "";
      if (status === 409 || /disponible|ocupado|no est/i.test(detail)) {
        pushBot("Ay, justo se ha ocupado ese hueco. Déjame que te proponga otro en la misma franja.");
        setConvoStage("proposing");
        const next = [...excluded, slot.start];
        setExcluded(next);
        await fetchProposal({ excludedList: next });
      } else if (status === 400 && /Ya tienes/i.test(detail)) {
        pushBot(
          "Ya tienes una cita reservada. Pulsa aquí abajo para gestionarla (cancelar o cambiar horario) y luego vuelve a reservar."
        );
        setConvoStage("has_prior_booking");
      } else {
        // Ronda 33.14 — fallo genérico (calendar 502, timeout, red). NO reseteamos
        // el flujo: mantenemos slot/nombre/teléfono y ofrecemos «Reintentar»
        // para que la clienta no tenga que renegociar la hora otra vez.
        pushBot(
          `Ay, no he podido cerrar la reserva ahora mismo: ${detail || "prueba de nuevo en unos segundos"}. Tu hueco (${formatSpanishDate(slot.start)} a las ${formatTime(slot.start)}) no se ha perdido — pulsa «Reintentar» y lo intento otra vez.`,
          { type: "booking_failed_retry", slot }
        );
        setCurrentSlot(slot);
        setConvoStage("booking_failed");
      }
    }
    setLoading(false);
  };

  const rejectProposal = async (slot) => {
    pushUser("No me viene bien");
    await handleReject(slot);
  };

  const handleReject = async (slot) => {
    const next = [...excluded, slot.start];
    setExcluded(next);
    const nextCount = rejectCount + 1;
    setRejectCount(nextCount);
    if (nextCount >= 4) {
      pushBot(
        `He probado varias opciones ${band === "morning" ? "por la mañana" : "por la tarde"} y ninguna te encaja. Si me dices un día concreto (por ejemplo «el 22» o «el 22 de agosto») lo miro directamente en mi agenda. También puedes cambiar de franja diciéndome «mejor por ${band === "morning" ? "la tarde" : "la mañana"}».`
      );
      setConvoStage("proposing"); // stay open — client can still type dates or change band
      return;
    }
    pushBot(`Vale, miro otro hueco ${band === "morning" ? "por la mañana" : "por la tarde"}, dame un segundo…`);
    await fetchProposal({ excludedList: next });
  };

  const askDisambiguation = (direction) => {
    setPendingShift(direction);
    const dirWord = direction === "earlier" ? "antes" : "después";
    pushBot(
      `¿Te refieres a un poco ${dirWord} ese mismo día, o prefieres una fecha ${direction === "earlier" ? "anterior" : "posterior"}?`,
      { type: "disambiguate", direction }
    );
    setConvoStage("disambiguate_shift");
  };

  const resolveDisambiguation = async (kind, silent = false) => {
    // kind: 'time' | 'date'
    const dir = pendingShift;
    setPendingShift(null);
    if (!silent) pushUser(kind === "time" ? "En el mismo día" : "En otra fecha");
    setConvoStage("proposing");
    if (kind === "time") {
      setConvoStage("ask_time");
      pushBot("¿A qué hora te vendría bien ese día?");
      return;
    }
    if (!currentSlot || !dir) return;
    const next = [...excluded, currentSlot.start];
    setExcluded(next);
    await fetchProposal({
      excludedList: next,
      shift: dir,
      shift_kind: kind,
      anchor: currentSlot.start,
    });
  };

  const proposeSpecificDate = async (day, month, modifier = null, overrideBand = null) => {
    setLoading(true);
    try {
      // Ronda 30: overrideBand permite forzar la franja para esta búsqueda
      // aunque el state React aún no haya propagado el `setBand(...)`.
      const effectiveBand = overrideBand || band;
      // Ronda 31.6: NO enviamos `excluded` en preguntas de fecha específica
      // ni con modificador "before/after". La clienta está EXPLORANDO una
      // ventana concreta y previamente ofrecidos que ella rechazó pueden
      // volver a ser candidatos válidos. Enviarlos causaba la contradicción
      // «antes del 4 no hay nada» cuando en realidad sí había fechas antes
      // que ya habíamos ofrecido y descartado.
      const body = { band: effectiveBand, excluded: [], prefer_day: day };
      if (month) body.prefer_month = month;
      if (modifier) body.prefer_modifier = modifier;
      const { data: r } = await api.post("/booking/propose-slot", body);
      // Ronda 32.12: usar `effectiveBand` en los mensajes (no el state
      // `band`, que aún no se ha propagado si venimos de un cambio de
      // franja en la misma acción). Antes, la clienta pedía "el 25 por la
      // mañana" y el bot respondía "no tengo hueco por la tarde" porque
      // el state seguía siendo el anterior.
      const bandLabel = effectiveBand === "morning" ? "por la mañana" : "por la tarde";
      if (!r.slot) {
        if (r.reason === "nothing_before") {
          pushBot(`No tengo ningún hueco ${bandLabel} antes del día ${day}. La próxima fecha disponible es a partir del día ${day} en adelante. Si quieres, dime otro día o probamos con «después del día ${day}».`);
        } else if (r.reason === "nothing_after") {
          pushBot(`No tengo huecos ${bandLabel} después del día ${day} en las próximas semanas. Dime otro día si prefieres.`);
        } else {
          pushBot("No tengo huecos disponibles en las próximas semanas para esa franja. Dime si prefieres cambiar de franja o intenta con otro día.");
        }
        setLoading(false);
        return;
      }
      // Ronda 32.22 (P3): la banda pedida está llena ese día, pero la
      // OTRA banda del mismo día tiene hueco. Anunciamos el matiz y
      // cambiamos la franja local para que el resto del flujo
      // (rechazos, cambios) sea coherente con el slot ofrecido.
      if (r.on_requested_date && r.outside_preferred_window && r.alt_band && r.alt_band !== effectiveBand) {
        const askedBandLabel = effectiveBand === "morning" ? "por la mañana" : "por la tarde";
        const altBandLabel = r.alt_band === "morning" ? "por la mañana" : "por la tarde";
        pushBot(
          `Ese día ${askedBandLabel} no me queda hueco, pero ${altBandLabel} podría ser el ${formatSpanishDate(r.slot.start)} a las ${formatTime(r.slot.start)} — ¿te encaja?`,
          { type: "slot", slot: r.slot }
        );
        setBand(r.alt_band);
        setExcluded([]);
        setCurrentSlot(r.slot);
        setConvoStage("proposing");
        setRejectCount(0);
        setLoading(false);
        return;
      }
      if (r.on_requested_date) {
        pushBot(`¿Te viene bien el ${formatSpanishDate(r.slot.start)} a las ${formatTime(r.slot.start)}? Si te encaja, dímelo y lo cerramos.`, { type: "slot", slot: r.slot });
      } else if (r.modifier === "before") {
        pushBot(
          `El hueco disponible más cercano ${bandLabel} antes del día ${day} es el ${formatSpanishDate(r.slot.start)} a las ${formatTime(r.slot.start)} — ¿te encaja?`,
          { type: "slot", slot: r.slot }
        );
      } else if (r.modifier === "after") {
        pushBot(
          `Después del día ${day}, el hueco más cercano ${bandLabel} es el ${formatSpanishDate(r.slot.start)} a las ${formatTime(r.slot.start)} — ¿te encaja?`,
          { type: "slot", slot: r.slot }
        );
      } else {
        pushBot(
          `Ese día no tengo hueco libre ${bandLabel}. La fecha más próxima que sí tengo disponible es el ${formatSpanishDate(r.slot.start)} a las ${formatTime(r.slot.start)} — ¿te encaja?`,
          { type: "slot", slot: r.slot }
        );
      }
      setCurrentSlot(r.slot);
      setConvoStage("proposing");
      setRejectCount(0);
    } catch (e) {
      if (e?.response?.status === 503) setCurrentSlot(null);
      pushBot(calendarErrorText(e, "Ha habido un problema comprobando esa fecha. Prueba a decirme el día de otra forma."), { type: "calendar_error" });
    }
    setLoading(false);
  };

  const resumeQuestion = (stage = convoStage) => {
    if (currentSlot && stage === "proposing") return `¿te viene bien el ${formatSpanishDate(currentSlot.start)} a las ${formatTime(currentSlot.start)}?`;
    if (stage === "ask_band") return "¿te viene mejor por la mañana o por la tarde?";
    return "¿qué día te vendría bien?";
  };

  const offerSlot = (text, slot) => {
    pushBot(text, { type: "slot", slot });
    setCurrentSlot(slot);
    setConvoStage("proposing");
    setRejectCount(0);
  };

  const askExplanation = (slot, withEmail) => {
    const base = formatTime(slot.start);
    const req = slot.time_change.requested;
    return withEmail
      ? `Para que no pierdas el hueco, pulsa el botón de las ${base} y te lo dejo reservado. Mientras, le pregunto a Bea si puede a las ${req}. Si puede, te llegará un email cambiándote la cita. Si no te llega ningún email, es que no ha podido y tu cita se queda a las ${base}.`
      : `Para que no pierdas el hueco, pulsa el botón de las ${base} y te lo dejo reservado. Mientras, le pregunto a Bea si puede a las ${req}. Si no nos escribes por WhatsApp, tu cita se queda a las ${base}.`;
  };

  const handleTimeRequest = async ({ time, day, month, anchor }) => {
    setLoading(true);
    try {
      const { data: r } = await api.post("/booking/time-request", { time, day, month, anchor });
      const slotBand = r.slot && new Date(r.slot.start).getHours() < 15 ? "morning" : "afternoon";
      if (r.slot) { setBand(slotBand); setExcluded([]); }
      if (r.mode === "direct") {
        offerSlot(`¡Claro! Te puedo dar el ${formatSpanishDate(r.slot.start)} a las ${formatTime(r.slot.start)} — ¿te encaja?`, { ...r.slot, time_change: { mode: "direct" } });
      } else if (r.mode === "ask") {
        const askSlot = { ...r.slot, time_change: { mode: "ask", requested: r.requested, requested_start: r.requested_slot.start, requested_end: r.requested_slot.end } };
        const prev = currentSlot?.time_change;
        if (prev?.mode === "ask" && currentSlot.start === r.slot.start && prev.requested === r.requested) {
          // Ya explicado: no repetimos la pregunta.
          const finalStage = convoStage === "awaiting_final_confirmation" || stageBeforeRef.current === "awaiting_final_confirmation";
          if (finalStage) setConvoStage("awaiting_final_confirmation");
          pushBot(
            `Sí, ya le anoto a Bea que prefieres las ${r.requested}. Mientras, te dejo asegurada la de las ${formatTime(r.slot.start)}.`,
            finalStage ? { type: "final_confirmation", slot: currentSlot } : { type: "slot", slot: currentSlot }
          );
        } else if (!clientEmail) {
          pendingAskRef.current = askSlot;
          setCurrentSlot(askSlot);
          setConvoStage("ask_tc_email");
          pushBot(`Puedo preguntarle a Bea si puede a las ${r.requested}. ¿A qué email te aviso?`);
        } else {
          offerSlot(askExplanation(askSlot, true), askSlot);
        }
      } else if (r.mode === "exact") {
        offerSlot(`Sí, el ${formatSpanishDate(r.slot.start)} a las ${formatTime(r.slot.start)} lo tengo libre — ¿te encaja?`, r.slot);
      } else if (r.mode === "not_possible") {
        offerSlot(
          r.same_day
            ? `A esa hora no es posible. Lo más cercano que tengo ese día es a las ${formatTime(r.slot.start)} — ¿te encaja?`
            : `A esa hora no es posible, y ese día no me queda hueco. Lo más cercano es el ${formatSpanishDate(r.slot.start)} a las ${formatTime(r.slot.start)} — ¿te encaja?`,
          r.slot
        );
      } else {
        pushBot("Ahora mismo no encuentro hueco cerca de esa hora. Dime otro día y lo miro.");
      }
    } catch (e) {
      pushBot(calendarErrorText(e, "Ha habido un problema comprobando esa hora. ¿Me la dices de otra forma?"), { type: "calendar_error" });
    }
    setLoading(false);
  };

  const sendEscalation = async (contact, email) => {
    setLoading(true);
    const userMsgs = escalateRequestRef.current || chatLog.filter((m) => m.role === "user").slice(-3).map((m) => m.text).join("\n");
    try {
      await api.post("/booking/escalate", {
        phone, name: name.trim(), contact, email,
        request: userMsgs,
        transcript: chatLog.map((m) => ({ role: m.role, text: m.text })),
      });
      pushBot(`Hecho, se lo he pasado a Bea. Si hay cualquier cambio, el equipo de Bea te contactará ${contact === "email" ? `por email (${email})` : "por WhatsApp o teléfono"}; si no, todo se queda como está.`);
    } catch (e) {
      pushBot("Ay, no he podido pasárselo ahora mismo. Si quieres, escríbenos por WhatsApp y lo vemos.");
    }
    setConvoStage(escalateReturnStage || "proposing");
    setLoading(false);
  };

  const answerEscalation = (yes) => {
    pushUser(yes ? "Sí, pásaselo a Bea" : "No, seguimos aquí");
    if (!yes) {
      const back = escalateReturnStage || "proposing";
      setConvoStage(back);
      const q = resumeQuestion(back).replace(/^(¿?)(.)/, (_, a, c) => a + c.toUpperCase());
      const meta = back === "ask_band" ? { type: "band_question" } : (back === "proposing" && currentSlot ? { type: "slot", slot: currentSlot } : undefined);
      pushBot(`Vale, seguimos. ${q}`, meta);
      return;
    }
    setConvoStage("escalate_contact");
    pushBot("¿Cómo prefieres que te contactemos si hay novedades?", { type: "escalate_contact" });
  };

  const chooseContact = async (kind) => {
    pushUser(kind === "email" ? "Por email" : "Por WhatsApp o teléfono");
    if (kind === "email") {
      if (clientEmail && clientEmail.includes("@")) { await sendEscalation("email", clientEmail); return; }
      setConvoStage("escalate_email");
      pushBot("Perfecto. Escríbeme tu email aquí abajo.");
      return;
    }
    await sendEscalation("whatsapp");
  };

  const sendFreeText = async () => {
    const t = chatText.trim();
    if (!t || loading) return;
    setChatText("");
    pushUser(t);
    setLoading(true);

    // await_receipt / receipt_sent: never call chat-interpret, just reply politely
    // Ronda 32.13: detectar intención de CAMBIO de fecha/hora post-confirmación
    // → redirigir a «Gestionar mi cita» con calidez. NO reabre la negociación.
    const _wantsDateChangePostConfirm = (low) => (
      /(camb\w+|mov\w+|otr[oa] (d[ií]a|hora|fecha)|adelanta\w*|atras\w*|posponer|adelantar|otro horario|cambiar.*(cita|d[ií]a|hora|fecha))/.test(low)
    );

    if (convoStage === "await_receipt") {
      const low = t.toLowerCase();
      // Bizum request (only if client asks explicitly)
      if (/(bizum|bisum|no puedo hacer transferencia|transferencia me resulta|es difícil.*transferencia|difícil.*transferencia|otra forma de pago)/.test(low)) {
        try {
          const { data: bz } = await api.post("/booking/bizum-request", booking?.phone ? { phone: booking.phone } : {});
          pushBot(
            `Sí, de forma puntual también puedes hacerlo por Bizum al número ${bz.number}. Cuando lo hayas hecho, sube igualmente el justificante y lo confirmamos.`,
            { type: "bizum_offered" }
          );
        } catch (err) {
          pushBot("Sí, de forma puntual puedes pagar por Bizum. Escríbeme por WhatsApp y te paso el número.");
        }
        setLoading(false);
        return;
      }
      if (low.includes("cancel") || low.includes("anul")) {
        pushBot("Si quieres cancelarla, vuelve a entrar con tu teléfono desde «Gestionar mi cita» y podrás anularla o cambiarla en un momento. Aquí sigo por si tienes más dudas.");
        setLoading(false);
        return;
      }
      if (_wantsDateChangePostConfirm(low)) {
        // Ronda 32.13: intento explícito de mover la cita ya reservada.
        pushBot(
          "Como el hueco ya está apuntado a tu nombre, para cambiarlo de día u hora lo mejor es que lo hagas desde «Gestionar mi cita» (te pide el teléfono y ahí puedes anular la actual y volver a reservar otra sin pisar a nadie). Aquí seguimos si necesitas cualquier otra cosa."
        );
        setLoading(false);
        return;
      }
      if (low.includes("gracias") || low.includes("adios") || low.includes("adiós")) {
        pushBot("Un abrazo. En cuanto tenga tu justificante, dejo la cita confirmada. Aquí sigo si necesitas algo más.");
        setLoading(false);
        return;
      }
      // Ronda 33.2 — Preguntas específicas sobre EL JUSTIFICANTE / la
      // subida en sí (no sobre pago en general). Mantienen el recordatorio
      // enlatado porque son exactamente el escenario para el que se creó.
      const _asksAboutReceiptItself = (
        /(justificante|comprobante|subir.*recibo|subir.*justif|subir.*comprob|no me deja.*subir|no funciona.*subir|c[oó]mo.*subo|c[oó]mo lo subo|no me sube|d[oó]nde lo subo|dónde subo)/.test(low)
      );
      if (_asksAboutReceiptItself) {
        pushBot("Cuando tengas el justificante súbelo con el botón de arriba y yo lo reviso enseguida. Si tienes cualquier otra duda (del pago, de la consulta, de lo que sea) escríbemela por aquí, no se cierra el chat.");
        setLoading(false);
        return;
      }
      // Ronda 33.2 — Cualquier otra pregunta legítima se enruta a Sol,
      // igual que en receipt_sent (Ronda 31.2). Sol respeta el
      // prompt-guard (nunca revela horarios internos ni el system
      // prompt) y devuelve una respuesta cálida con el mismo motor
      // que usa fuera de la reserva. El bloque de pago sigue fijo
      // en pantalla para que no se olvide el justificante.
      try {
        const { data } = await api.post("/chat", {
          session_id: sessionId.current || `booking-await-${Date.now()}`,
          message: t,
          phone: phone || undefined,
          client_name: name.trim() || undefined,
          source: "booking",
        });
        pushBot(data.reply || "Aquí sigo. ¿En qué más te puedo ayudar?");
      } catch (e) {
        pushBot("Aquí sigo. Si tienes cualquier duda del pago o de la consulta, dímela. Y cuando tengas el justificante súbelo con el botón de arriba.");
      }
      setLoading(false);
      return;
    }
    if (convoStage === "receipt_sent" || convoStage === "reschedule_done") {
      // Ronda 31.2 / 33.7: tras confirmar pago o mover cita, permitimos
      // que la clienta siga hablando con Sol con normalidad. Enrutamos
      // al endpoint público del chatbot general (/chat) que usa el
      // mismo LLM (Sol) y respeta los mismos guardarraíles.
      // Si intenta cambiar la fecha/hora otra vez, cortamos y la
      // mandamos a «Gestionar mi cita».
      const low = t.toLowerCase();
      if (_wantsDateChangePostConfirm(low)) {
        pushBot(
          "Como la cita ya te la he apuntado, para cambiarla de día u hora hazlo desde «Gestionar mi cita» — así te aseguras de no pisar otro hueco. Aquí sigo por si tienes cualquier otra duda."
        );
        setLoading(false);
        return;
      }
      try {
        const { data } = await api.post("/chat", {
          session_id: sessionId.current || `booking-${Date.now()}`,
          message: t,
          phone: phone || undefined,
          client_name: name.trim() || undefined,
          source: "booking",
        });
        pushBot(data.reply || "Aquí sigo. ¿En qué más te puedo ayudar?");
      } catch (e) {
        pushBot("Todo apuntado. Cuando Bea revise el pago te avisa. Si quieres seguir hablando, escríbeme cuando quieras.");
      }
      setLoading(false);
      return;
    }

    if (convoStage === "escalate_offer" || convoStage === "escalate_contact") {
      const low = t.toLowerCase();
      setLoading(false);
      if (convoStage === "escalate_offer") {
        if (/^(s[ií]|vale|ok|claro|de acuerdo|perfecto)/.test(low)) { answerEscalation(true); return; }
        if (/^no\b/.test(low)) { answerEscalation(false); return; }
      } else {
        if (/mail|correo/.test(low)) { await chooseContact("email"); return; }
        if (/whats|tel[eé]fono|llam|m[oó]vil/.test(low)) { await chooseContact("whatsapp"); return; }
      }
      pushBot(convoStage === "escalate_offer" ? "¿Quieres que se lo pase a Bea? Dime «sí» o «no»." : "¿Prefieres que te contactemos por WhatsApp/teléfono o por email?");
      return;
    }
    if (convoStage === "ask_tc_email" && pendingAskRef.current) {
      const em = t.trim();
      const slot = pendingAskRef.current;
      setLoading(false);
      if (/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(em)) {
        setClientEmail(em);
        pendingAskRef.current = null;
        offerSlot(askExplanation(slot, true), slot);
        return;
      }
      if (/\bno\b|prefiero no|sin email|no tengo|no quiero/i.test(em)) {
        pendingAskRef.current = null;
        pushBot("Si lo prefieres, escríbenos por WhatsApp desde el botón de la web diciendo que has pedido un cambio de hora, y te contestamos por ahí.");
        offerSlot(askExplanation(slot, false), slot);
        return;
      }
      pushBot("Ese email no me parece válido. ¿Me lo escribes de nuevo? Si prefieres no darlo, dime «no».");
      return;
    }
    if (convoStage === "escalate_email") {
      const em = t.trim();
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(em)) {
        pushBot("Ese email no me parece válido. ¿Me lo escribes de nuevo?");
        setLoading(false);
        return;
      }
      await sendEscalation("email", em);
      return;
    }
    if (convoStage === "ask_time") {
      const m = t.toLowerCase().match(/(\d{1,2})(?:\s*[:.h]\s*(\d{2}))?/);
      if (m) {
        let hh = parseInt(m[1], 10);
        const mm = m[2] ? parseInt(m[2], 10) : 0;
        if (hh <= 7 || (hh <= 11 && /tarde|noche/.test(t.toLowerCase()))) hh += 12;
        setConvoStage("proposing");
        await handleTimeRequest({ time: `${String(hh).padStart(2, "0")}:${String(mm).padStart(2, "0")}`, anchor: currentSlot?.start });
        return;
      }
      setConvoStage("proposing");
    }
    const prevMiss = missRef.current;
    missRef.current = 0;
    const notUnderstood = () => {
      missRef.current = prevMiss + 1;
      if (missRef.current < 2) return false;
      missRef.current = 0;
      escalateRequestRef.current = [...chatLog.filter((m) => m.role === "user").slice(-2).map((m) => m.text), t].join("\n");
      setEscalateReturnStage(convoStage === "disambiguate_shift" ? "proposing" : convoStage);
      setConvoStage("escalate_offer");
      pushBot("Perdona, no termino de entenderte. Si quieres, se lo paso a Bea y el equipo de Bea te contacta.", { type: "escalate_offer" });
      setLoading(false);
      return true;
    };
    try {
      // If we're EXHAUSTED and the client says "whatsapp" explicitly, allow it.
      // Otherwise, call chat-interpret so specific-date and other intents work.
      const { data } = await api.post("/booking/chat-interpret", {
        session_id: sessionId.current || `booking-${Date.now()}`,
        message: t,
        current_slot_start: currentSlot?.start,
        band,
        stage: convoStage === "exhausted" ? "proposing" : convoStage,
        excluded,
        phone: phone || undefined,
      });
      const action = data.action;

      // Ronda 31.4 + 32.10: acciones informativas (call-direction, off-topic
      // redirect a Sol u otras Q&A del catálogo determinístico). Devuelven
      // directamente el `message` como respuesta de Sol sin alterar el flujo
      // de reserva. off_topic añade además un botón visual para abrir el
      // chat de Sol.
      if (action === "off_topic") {
        pushBot(`¡Buena pregunta! Eso te lo cuenta Sol encantada, abajo a la derecha. Y mientras, seguimos con lo tuyo: ${resumeQuestion()}`, currentSlot && convoStage === "proposing" ? { type: "slot", slot: currentSlot } : { type: "open_sol_hint" });
        setLoading(false);
        return;
      }
      if (action === "info" && data.message) {
        pushBot(data.message);
        setLoading(false);
        return;
      }

      // Ronda 32.22 (P5): día inválido («el 226», «el 40», «día 0»). Se
      // muestra la aclaración SIN alterar el estado — sobre todo, sin
      // resetear `excluded`, para que las exclusiones ya acumuladas por
      // la clienta se mantengan y no volvamos a proponer huecos que
      // ella ya rechazó.
      if (action === "invalid_date" && data.message) {
        pushBot(data.message);
        setLoading(false);
        return;
      }

      // Hora concreta pedida por la clienta (cambio de hora a petición suya)
      if (action === "time_request" && data.time) {
        await handleTimeRequest({ time: data.time, anchor: convoStage === "ask_band" ? undefined : currentSlot?.start });
        return;
      }
      if (action === "specific_date" && data.day && data.time) {
        await handleTimeRequest({ time: data.time, day: data.day, month: data.month || undefined });
        return;
      }

      // Universal: specific-date works at ANY stage (including exhausted)
      if (action === "specific_date" && data.day && (band || data.band)) {
        setLoading(false);
        if (convoStage === "ask_band") setConvoStage("proposing");
        // Ronda 30: si el interpret trae también `band` (ej. "el 28 por la
        // tarde"), lo aplicamos ANTES de buscar el día concreto para que la
        // franja del resultado sea la que el usuario ha pedido, no la actual.
        if (data.band && data.band !== band) {
          setBand(data.band);
          setExcluded([]);
          await proposeSpecificDate(data.day, data.month || null, data.modifier || null, data.band);
        } else {
          await proposeSpecificDate(data.day, data.month || null, data.modifier || null);
        }
        return;
      }
      // Explicit WhatsApp request from client always honored
      if (action === "whatsapp") {
        pushBot("Perfecto, hablamos por WhatsApp. Aquí abajo tienes el botón.");
        setConvoStage("exhausted");
        setLoading(false);
        return;
      }

      // Exhausted stage: don't spam WhatsApp — invite to try another approach
      if (convoStage === "exhausted") {
        const low = t.toLowerCase();
        if (low.includes("gracias") || low.includes("adios") || low.includes("adiós") || low.includes("hasta")) {
          pushBot("Un abrazo. Aquí me tienes cuando lo necesites.");
        } else if (action === "morning" || action === "afternoon" || action === "change_band") {
          const newBand = action === "change_band" ? data.band : action;
          if (newBand && newBand !== band) {
            pushBot(`Vale, cambiamos a ${newBand === "morning" ? "la mañana" : "la tarde"} y volvemos a mirar.`);
            setBand(newBand);
            setExcluded([]);
            setRejectCount(0);
            setLoading(false);
            await fetchProposal({ band: newBand, excludedList: [] });
            return;
          }
        } else {
          pushBot("Si me dices un día concreto (por ejemplo «el día 22» o «el 22 de agosto») lo miro en mi agenda. También puedes pulsar «Empezar de nuevo» aquí abajo.");
        }
        setLoading(false);
        return;
      }

      // Ronda 32.15 (fix de Amy): NO propagar `data.message` a la UI para
      // las acciones del clasificador (reject, change_band, shift_*,
      // specific_date, ambiguous_*, different_day, confirm, whatsapp,
      // unknown). Cada rama abajo genera su propio texto visible. La
      // única excepción son `info` y `off_topic`, que ya se manejan
      // arriba (líneas 477-481) porque el mensaje viene de un
      // interceptor determinístico controlado, no del LLM.
      //
      // Antes había aquí:
      //   if (data.message) pushBot(data.message);
      // Esa línea es la que dejaba escapar etiquetas internas del LLM
      // ("Pregunta sobre agenda personal de Bea", "Clasificación: X"…)
      // al chat de la clienta.

      // ---- Stage: ask_band ----
      if (convoStage === "ask_band") {
        if (action === "morning" || action === "afternoon") {
          setLoading(false);
          await pickBand(action);
          return;
        }
        // Ronda 32.20: expresiones de "sin preferencia real" → cita más pronto
        if (action === "any_band") {
          setLoading(false);
          // Ronda 33.3 (2026-02): urgencia explícita — reconocemos el
          // matiz antes de la búsqueda, como pidió Bea, para que la
          // clienta sepa que se la ha escuchado.
          if (data.urgent) {
            pushBot("Entiendo que corre prisa. Te busco lo más pronto que tenga, dame un segundo…");
          } else {
            pushBot("Perfecto, te propongo la primera cita disponible, dame un segundo…");
          }
          setBand("any");
          setExcluded([]);
          setConvoStage("proposing");
          await fetchProposal({ band: "any", excludedList: [] });
          return;
        }
        // Ronda 32.20: "no veo los botones" → repetimos la pregunta por texto,
        // SIN mencionar los botones (que efectivamente pueden no estar visibles).
        if (action === "repeat_band_question_text") {
          pushBot('¿Prefieres mañana o tarde? Dímelo con «mañana», «tarde» o «me da igual».');
          setLoading(false);
          return;
        }
        if (notUnderstood()) return;
        pushBot('¿Prefieres mañana o tarde? Dímelo con «mañana», «tarde» o «me da igual».');
        setLoading(false);
        return;
      }

      // ---- Stage: disambiguate_shift ----
      if (convoStage === "disambiguate_shift") {
        if (action === "shift_time" || action === "shift_earlier_time" || action === "shift_later_time") {
          setLoading(false);
          await resolveDisambiguation("time");
          return;
        }
        if (action === "shift_date" || action === "shift_earlier_date" || action === "shift_later_date" || action === "different_day") {
          setLoading(false);
          await resolveDisambiguation("date");
          return;
        }
        // Nunca volvemos a preguntar lo mismo: «otro día/fecha» → fecha; lo demás → mismo día.
        setLoading(false);
        await resolveDisambiguation(/otr[oa]s? (d[ií]a|fecha)|fecha (distinta|anterior|posterior)|otro d[ií]a/.test(t.toLowerCase()) ? "date" : "time", true);
        return;
      }

      // ---- Stage: proposing ----
      // Ronda 33.1: en awaiting_final_confirmation, sólo confirm/reject
      // son válidos; cualquier otra intención devuelve al stage proposing
      // sin crear la reserva.
      if (convoStage === "awaiting_final_confirmation" && action !== "confirm" && action !== "reject") {
        stageBeforeRef.current = "awaiting_final_confirmation";
        setConvoStage("proposing");
        // se sigue procesando la acción normal en las ramas de abajo
      } else {
        stageBeforeRef.current = null;
      }

      if (action === "confirm" && currentSlot) {
        if (convoStage === "awaiting_final_confirmation") {
          // Segunda confirmación explícita → AHORA sí se reserva.
          await finalizeBooking(currentSlot);
        } else {
          // Primera confirmación → pedimos segunda antes de escribir en calendar.
          await acceptProposal(currentSlot);
        }
        return;
      }
      if (action === "reject" && currentSlot) {
        if (convoStage === "awaiting_final_confirmation") {
          cancelFinalConfirmation();
          return;
        }
        await handleReject(currentSlot);
        return;
      }
      if (action === "ambiguous_earlier") {
        askDisambiguation("earlier");
        setLoading(false);
        return;
      }
      if (action === "ambiguous_later") {
        askDisambiguation("later");
        setLoading(false);
        return;
      }
      if (action === "shift_earlier_time" || action === "shift_later_time") {
        const dir = action === "shift_earlier_time" ? "earlier" : "later";
        const next = [...excluded, currentSlot.start];
        setExcluded(next);
        await fetchProposal({ excludedList: next, shift: dir, shift_kind: "time", anchor: currentSlot.start });
        return;
      }
      if (action === "shift_earlier_date" || action === "shift_later_date") {
        const dir = action === "shift_later_date" ? "later" : "earlier";
        // Ronda 32.21 Fix 2: al cambiar dirección/fecha, RESET de excluded.
        // El pre-filtro direccional del backend ya evita ofrecer el mismo
        // slot; arrastrar excluded bloquea huecos legítimos en conversaciones
        // largas con varios cambios de fecha.
        setExcluded([]);
        await fetchProposal({ excludedList: [], shift: dir, shift_kind: "date", anchor: currentSlot.start });
        return;
      }
      if (action === "different_day") {
        // Ronda 32.21 Fix 2 (revisado tras verificación e2e):
        // "otro día" quiere reset del histórico PERO manteniendo el slot
        // actual como excluido — si no, el backend re-ofrece el mismo
        // slot que la clienta acaba de rechazar (paso 4b del test real).
        const next = currentSlot ? [currentSlot.start] : [];
        setExcluded(next);
        await fetchProposal({ excludedList: next });
        return;
      }
      if (action === "any_band") {
        pushBot("Te miro la primera cita disponible, dame un segundo…");
        setBand("any");
        setExcluded([]);
        setRejectCount(0);
        await fetchProposal({ band: "any", excludedList: [] });
        return;
      }
      if (action === "change_band" && data.band && data.band !== band) {
        pushBot(`Cambiamos a ${data.band === "morning" ? "la mañana" : "la tarde"}.`);
        setBand(data.band);
        setExcluded([]);
        setRejectCount(0);
        await fetchProposal({ band: data.band, excludedList: [] });
        return;
      }
      if (notUnderstood()) return;
      pushBot(_nextFallbackVariant(fallbackIdxRef));
    } catch (e) {
      pushBot("Ay, algo ha fallado por mi lado. Prueba con los botones o con una respuesta más corta, y si sigues sin poder tienes a Sol abajo a la derecha para preguntarle lo que quieras.");
    }
    setLoading(false);
  };

  const uploadReceipt = async (file) => {
    if (!file || !booking) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("phone", phone);
    fd.append("event_id", booking.event_id);
    fd.append("file", file);
    if (clientEmail && clientEmail.includes("@")) fd.append("client_email", clientEmail);
    try {
      await api.post("/booking/upload-receipt", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Justificante recibido. Vamos a revisar tu pago.");
      setStep("done");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error subiendo el justificante");
    }
    setUploading(false);
  };

  const uploadReceiptInChat = async (file) => {
    if (!file || !booking) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("phone", phone);
    fd.append("event_id", booking.event_id);
    fd.append("file", file);
    if (clientEmail && clientEmail.includes("@")) fd.append("client_email", clientEmail);
    try {
      await api.post("/booking/upload-receipt", fd, { headers: { "Content-Type": "multipart/form-data" } });
      pushUser("[Justificante enviado]");
      pushBot(
        clientEmail
          ? "Recibido, muchas gracias. En cuanto revise el pago te llegará un email con la confirmación definitiva de tu cita."
          : "Recibido, muchas gracias. En cuanto revise el pago dejaré la cita confirmada; puedes volver a entrar aquí con tu teléfono para ver el estado.",
        { type: "receipt_received" }
      );
      setConvoStage("receipt_sent");
      toast.success("Justificante recibido.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error subiendo el justificante");
    }
    setUploading(false);
  };

  const uploadReceiptForExisting = async (file) => {
    if (!file || !appointment) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("phone", phone);
    fd.append("event_id", appointment.event_id);
    fd.append("file", file);
    if (clientEmail && clientEmail.includes("@")) fd.append("client_email", clientEmail);
    try {
      await api.post("/booking/upload-receipt", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Justificante recibido. Vamos a revisar tu pago cuanto antes.");
      setUploadFromExisting("sent");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error subiendo el justificante");
    }
    setUploading(false);
  };

  const doCancel = async () => {
    if (!cancelReason || cancelReason.trim().length < 3) {
      toast.error("Cuéntame brevemente el motivo, por favor.");
      return;
    }
    setLoading(true);
    try {
      await api.post("/booking/cancel", { phone, event_id: appointment.event_id, reason: cancelReason });
      toast.success("Cita cancelada.");
      setAppointment(null);
      setShowCancel(false);
      setCancelReason("");
      setStep("conversation");
      setConvoStage("intro");
      newSession();
      setChatLog([]);
      setExcluded([]);
      setBand(null);
      setCurrentSlot(null);
      setRejectCount(0);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    }
    setLoading(false);
  };

  const rescheduleFlow = async () => {
    if (!cancelReason || cancelReason.trim().length < 3) {
      toast.error("Cuéntame el motivo del cambio.");
      return;
    }
    // Ronda 33.4 — NO cancelamos la antigua todavía. Abrimos el chat
    // en modo reschedule; el cambio será atómico en finalizeBooking →
    // POST /booking/reschedule (crear-antes-de-borrar con rollback).
    setMode("reschedule");
    setOldAppointment(appointment);
    setStep("conversation");
    setConvoStage("ask_band");
    const oldWhen = appointment?.start
      ? `del ${formatSpanishDate(appointment.start)} a las ${formatTime(appointment.start)}`
      : "actual";
    pushBot(
      `Vale, buscamos otro hueco para tu cita ${oldWhen}. La antigua sólo se cancela cuando cerremos la nueva — así no te quedas sin cita si algo sale mal. ¿La prefieres por la mañana, por la tarde, o me da igual y te propongo la primera libre?`,
      { type: "ask_band_reschedule" }
    );
  };

  const restartConversation = () => {
    newSession();
    setChatLog([]);
    setExcluded([]);
    setBand(null);
    setCurrentSlot(null);
    setPendingShift(null);
    setRejectCount(0);
    setConvoStage("intro");
  };

  return (
    <div className="min-h-screen relative">
      <div className="astral-bg"></div>
      <div className="astral-bloom"></div>
      <WhatsAppQRModal open={showQR} onClose={() => setShowQR(false)} />
      <header className="glass sticky top-0 z-40">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="font-serif text-2xl gold" data-testid="brand-link">Bea de Ibiza</Link>
          <a href="/#quien-soy" className="text-sm text-[#3B342A] hover:text-[#B8912E] transition-colors" data-testid="header-quien-link">
            Quién soy
          </a>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-12 relative z-10">
        <button onClick={() => nav("/")} className="flex items-center gap-2 text-sm text-[#3B342A] hover:text-[#B8912E] transition-colors mb-8" data-testid="back-home">
          <ArrowLeft size={14} /> Volver
        </button>

        {step === "phone" && config?.vacation_mode && (
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            <div className="card-gold mb-6" data-testid="vacation-banner">
              <div className="label-eyebrow">Modo vacaciones</div>
              <h2 className="font-serif text-2xl mt-3 text-[#141210]">Bea está de vacaciones unos días</h2>
              <p className="text-[#3B342A] mt-3 leading-relaxed whitespace-pre-line">{config.vacation_message}</p>
            </div>
            {waitlistSubmitted ? (
              <div className="card" data-testid="waitlist-confirmation">
                <span className="label-eyebrow">Estás en la lista</span>
                <h1 className="font-serif text-3xl md:text-4xl font-light mt-3 text-[#141210]">Te avisaremos cuando Bea vuelva.</h1>
                <p className="text-[#6C6355] mt-3 text-sm">
                  Respetamos estrictamente el orden de llegada. En cuanto Bea abra la agenda de nuevo, {waitlistEmail ? <>recibirás un email en <span className="gold font-medium">{waitlistEmail}</span> con las instrucciones para reservar tu cita.</> : <>Bea te avisará por WhatsApp al número que nos has dejado.</>}
                </p>
                <div className="mt-6">
                  <button onClick={() => nav("/")} className="btn-ghost" data-testid="waitlist-back-home">Volver a inicio</button>
                </div>
              </div>
            ) : (
              <div className="card" data-testid="waitlist-form">
                <span className="label-eyebrow">Lista de espera</span>
                <h1 className="font-serif text-3xl md:text-4xl font-light mt-3 text-[#141210]">Apúntate y te aviso cuando vuelva.</h1>
                <p className="text-[#6C6355] mt-3 text-sm">
                  Mientras esté fuera no puedo aceptar citas nuevas, pero puedes dejarme tus datos y en cuanto abra la agenda de nuevo Bea te avisará por WhatsApp respetando el orden de llegada. Si nos dejas email, también recibirás un aviso automático cuando la agenda se abra.
                </p>
                <div className="mt-6 space-y-3">
                  <input
                    data-testid="waitlist-name"
                    className="field"
                    placeholder="Tu nombre"
                    value={waitlistName}
                    onChange={(e) => setWaitlistName(e.target.value)}
                  />
                  <input
                    data-testid="waitlist-phone"
                    className="field"
                    placeholder="Teléfono (con +prefijo si es internacional)"
                    value={waitlistPhone}
                    onChange={(e) => setWaitlistPhone(e.target.value)}
                    inputMode="tel"
                  />
                  <input
                    data-testid="waitlist-email"
                    className="field"
                    placeholder="Email (opcional)"
                    value={waitlistEmail}
                    onChange={(e) => setWaitlistEmail(e.target.value)}
                    inputMode="email"
                    type="email"
                  />
                  <p className="text-xs text-[#6B6B6B] -mt-1" data-testid="waitlist-email-hint">
                    Necesario para recibir el aviso automático cuando Bea vuelva.
                  </p>
                  <button
                    data-testid="waitlist-submit"
                    className="btn-primary w-full"
                    onClick={submitWaitlist}
                    disabled={waitlistLoading}
                  >
                    {waitlistLoading ? "Apuntando…" : "Apuntarme a la lista de espera"}
                  </button>
                </div>
                <p className="text-xs text-[#8A8175] mt-4">
                  Si es urgente y no puedes esperar, escríbeme por el chat de esta web y te oriento con un gabinete de confianza.
                </p>
              </div>
            )}
          </motion.div>
        )}

        {step === "phone" && !config?.vacation_mode && (
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            <div className="card">
              <span className="label-eyebrow">Reservar cita</span>
              <h1 className="font-serif text-3xl md:text-4xl font-light mt-3 text-[#141210]">Empecemos con tu teléfono.</h1>
              <p className="text-[#6C6355] mt-3 text-sm">
                Con tu teléfono podrás también volver a cambiar o cancelar tu cita cuando quieras.
              </p>
              <div className="mt-8 flex gap-3">
                <input
                  data-testid="phone-input"
                  className="field"
                  placeholder="+34 6XX XXX XXX"
                  value={rawPhone}
                  onChange={(e) => setRawPhone(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && submitPhone()}
                  inputMode="tel"
                />
                <button data-testid="phone-submit" className="btn-primary whitespace-nowrap" onClick={submitPhone} disabled={loading}>
                  {loading ? "..." : "Continuar"}
                </button>
              </div>
              <p className="text-xs text-[#8A8175] mt-3 leading-relaxed" data-testid="phone-consent-notice">
                Al continuar aceptas nuestra{" "}
                <Link to="/privacidad" target="_blank" rel="noreferrer" className="underline hover:text-[#B8912E] transition-colors" data-testid="privacy-link-phone">
                  política de privacidad
                </Link>
                . Tu nombre queda asociado a este teléfono para reconocerte en tu próxima visita.
              </p>
            </div>
          </motion.div>
        )}

        <AnimatePresence mode="wait">
          {step === "existing" && appointment && (
            <motion.div key="existing" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}>
              <div className="card-gold">
                <span className="label-eyebrow">Tu próxima cita</span>
                {appointment.name && (
                  <div className="mt-3 text-[#3B342A] text-sm">Hola <span className="gold font-medium">{appointment.name}</span></div>
                )}
                <div className="mt-5 flex items-center gap-3">
                  <CalIcon className="gold" size={22} />
                  <div className="font-serif text-2xl md:text-3xl text-[#141210]">{formatSpanishDate(appointment.start)}</div>
                </div>
                <div className="mt-2 text-[#141210] text-xl">
                  {formatTime(appointment.start)} — {formatTime(appointment.end)}
                </div>
                <div className="mt-3 text-sm">
                  {appointment.confirmed ? (
                    <span className="text-emerald-600 flex items-center gap-2"><Check size={14}/> Confirmada (pagada)</span>
                  ) : (
                    <span className="text-[#B8912E]">Pendiente de justificante de pago</span>
                  )}
                </div>
                {!showCancel && (
                  <div className="mt-8 flex flex-wrap gap-3">
                    <button onClick={() => setShowCancel(true)} data-testid="cancel-btn" className="btn-ghost inline-flex items-center gap-2"><X size={16}/> Cancelar</button>
                    <button onClick={() => setShowCancel("reschedule")} data-testid="reschedule-btn" className="btn-primary inline-flex items-center gap-2"><RotateCw size={16}/> Cambiar horario</button>
                  </div>
                )}
              </div>

              {/* Cancellation / reschedule form — appears FIRST when the client
                  clicks Cancel/Reschedule, before anything else. The payment
                  upload block is intentionally hidden while cancelling. */}
              {showCancel && (
                <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="card-gold mt-6 border-red-300/40">
                  <div className="label-eyebrow">
                    {showCancel === "reschedule" ? "Motivo del cambio de horario" : "Motivo de la cancelación"}
                  </div>
                  <h3 className="font-serif text-2xl mt-3 text-[#141210]">
                    {showCancel === "reschedule"
                      ? "Cuéntame el motivo del cambio."
                      : "Cuéntame por qué necesitas cancelar."}
                  </h3>
                  <p className="text-sm text-[#6C6355] mt-2">
                    {showCancel === "reschedule"
                      ? "Cancelaremos la cita actual para que puedas elegir un horario nuevo."
                      : "Este dato me ayuda a llevar mi agenda con cuidado. Solo lo veo yo."}
                  </p>
                  <textarea
                    data-testid="cancel-reason"
                    className="field mt-4"
                    rows={4}
                    placeholder="Escribe aquí el motivo (mínimo unas palabras)…"
                    autoFocus
                    value={cancelReason}
                    onChange={(e) => setCancelReason(e.target.value)}
                  />
                  <div className="mt-5 flex flex-wrap gap-3">
                    <button
                      onClick={showCancel === "reschedule" ? rescheduleFlow : doCancel}
                      data-testid="confirm-cancel"
                      className="btn-primary inline-flex items-center gap-2"
                      disabled={loading || !cancelReason.trim() || cancelReason.trim().length < 3}
                    >
                      {loading ? "Enviando..." : (showCancel === "reschedule" ? "Confirmar cambio" : "Confirmar cancelación")}
                    </button>
                    <button
                      onClick={() => { setShowCancel(false); setCancelReason(""); }}
                      data-testid="cancel-abort"
                      className="btn-ghost"
                    >
                      Volver atrás
                    </button>
                  </div>
                </motion.div>
              )}

              {/* Payment receipt upload: ONLY visible when NOT cancelling and appointment is pending */}
              {!showCancel && !appointment.confirmed && (
                <div className="card mt-6" data-testid="existing-upload-card">
                  <div className="label-eyebrow">Justificante de pago</div>
                  <h3 className="font-serif text-xl mt-2 text-[#141210]">Sube aquí tu comprobante de pago.</h3>
                  <p className="text-sm text-[#6C6355] mt-2 leading-relaxed">
                    Cuando lo revise, te llegará el email de confirmación de la cita. Puedes hacer una foto en el momento o subirla desde tu galería.
                  </p>
                  {config?.payment_info && (
                    <div className="mt-4 p-4 rounded-xl bg-white border border-[#B8912E]/25 text-sm space-y-1">
                      <div><span className="text-[#6C6355]">Titular:</span> <span className="text-[#141210]">{config.payment_info.account_holder}</span></div>
                      <div><span className="text-[#6C6355]">IBAN:</span> <span className="gold font-medium">{config.payment_info.iban}</span></div>
                      <div><span className="text-[#6C6355]">Importe:</span> <span className="text-[#141210]">{config.consultation_price_eur}€</span></div>
                      <div><span className="text-[#6C6355]">Concepto:</span> <span className="text-[#141210]">{config.payment_info.concept}</span></div>
                    </div>
                  )}
                  {uploadFromExisting === "sent" ? (
                    <p className="mt-4 text-sm text-emerald-700 flex items-center gap-2"><Check size={14}/> Justificante recibido. Lo revisaré cuanto antes.</p>
                  ) : (
                    <label className="mt-5 block">
                      <div className="btn-primary inline-flex items-center gap-2 cursor-pointer w-full sm:w-auto justify-center" data-testid="existing-upload-btn">
                        <Upload size={16}/> {uploading ? "Subiendo..." : "Subir justificante"}
                      </div>
                      <input
                        type="file"
                        accept="image/*,application/pdf"
                        className="hidden"
                        data-testid="existing-receipt-upload"
                        onChange={(e) => uploadReceiptForExisting(e.target.files?.[0])}
                      />
                    </label>
                  )}
                </div>
              )}

              {/* Free-form note for Bea — always available, even after
                  confirmation, so the client can drop last-minute context
                  ("preferiría que me llames tú", etc.) */}
              {!showCancel && (
                <ClientNoteCard
                  phone={appointment.phone || phone}
                  eventId={appointment.event_id}
                />
              )}
            </motion.div>
          )}
          {step === "conversation" && (
            <motion.div key="conversation" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}>
              <span className="label-eyebrow">Reservar cita</span>
              <h2 className="font-serif text-3xl md:text-4xl font-light mt-3 text-[#0A0A0A]">
                Vamos a buscar <span className="gold italic">tu momento</span>.
              </h2>

              {convoStage === "intro" && (
                <div className="card mt-8 border-[#B8912E]/30" data-testid="convo-intro">
                  {returningClient && returningClient.name && (
                    <div
                      className="mb-4 p-3 rounded-xl bg-[#FFF9EB] border border-[#B8912E]/30 text-sm text-[#0A0A0A] leading-relaxed"
                      data-testid="returning-client-hello"
                    >
                      ¡Hola de nuevo, <strong>{returningClient.name}</strong>! Qué alegría verte por aquí otra vez. He rellenado tu nombre por ti — si no es correcto, cámbialo.
                    </div>
                  )}
                  <div className="label-eyebrow">Tus datos</div>
                  <div className="grid md:grid-cols-2 gap-3 mt-4">
                    <div>
                      <label className="text-xs text-[#6C6355] mb-1 block">Nombre*</label>
                      <div className="relative">
                        <User size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#A69C8A]"/>
                        <input
                          data-testid="name-input"
                          className="field pl-9"
                          placeholder="Tu nombre"
                          value={name}
                          onChange={(e) => setName(e.target.value)}
                        />
                      </div>
                    </div>
                    <div>
                      <label className="text-xs text-[#6C6355] mb-1 block">Email (opcional)</label>
                      <input
                        type="email"
                        data-testid="email-input-slots"
                        className="field"
                        placeholder="tucorreo@ejemplo.com"
                        value={clientEmail}
                        onChange={(e) => setClientEmail(e.target.value)}
                      />
                      <p className="text-xs text-[#8A8175] mt-2" data-testid="email-helper-text">
                        Si nos dejas tu email, te mandaremos la confirmación por ahí. Si no, te confirmamos directamente por este chat.
                      </p>
                    </div>
                  </div>
                  <p className="text-xs text-[#8A8175] mt-4">
                    Te propondré un momento concreto para tu cita. Si no te encaja, buscamos otro.
                  </p>
                  <div className="mt-6 flex justify-end">
                    <button
                      data-testid="convo-start-btn"
                      onClick={startConversation}
                      disabled={loading}
                      className="btn-primary inline-flex items-center gap-2"
                    >
                      <Sparkles size={16}/> {loading ? "..." : "Empezar"}
                    </button>
                  </div>
                </div>
              )}

              {convoStage !== "intro" && (
                <div className="card mt-8" data-testid="convo-chat">
                  <div className="space-y-3 max-h-[440px] overflow-y-auto pr-1">
                    {chatLog.map((m, i) => {
                      const isLast = i === chatLog.length - 1;
                      return (
                        <div key={m.id || `idx-${i}`}>
                          <div className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                            <div
                              className={`max-w-[85%] px-4 py-2.5 rounded-2xl text-[15px] leading-relaxed ${
                                m.role === "user"
                                  ? "bg-[#0A0A0A] text-white"
                                  : "bg-[#FFF7E5] border border-[#B8912E]/25 text-[#0A0A0A]"
                              }`}
                            >
                              {m.text}
                            </div>
                          </div>

                          {/* Band-question buttons */}
                          {isLast && m.role === "bot" && m.meta?.type === "band_question" && convoStage === "ask_band" && (
                            <div className="mt-3 flex flex-wrap gap-2 justify-start ml-1" data-testid="band-buttons">
                              <button onClick={() => pickBand("morning")} disabled={loading} data-testid="band-morning" className="btn-primary inline-flex items-center gap-2 text-sm">
                                <Sun size={14}/> Por la mañana
                              </button>
                              <button onClick={() => pickBand("afternoon")} disabled={loading} data-testid="band-afternoon" className="btn-primary inline-flex items-center gap-2 text-sm">
                                <Moon size={14}/> Por la tarde
                              </button>
                            </div>
                          )}

                          {/* Slot proposal buttons */}
                          {isLast && m.role === "bot" && m.meta?.type === "slot" && convoStage === "proposing" && (
                            <div className="mt-3 flex flex-wrap gap-2 justify-start ml-1" data-testid="convo-buttons">
                              <button onClick={() => acceptProposal(m.meta.slot)} disabled={loading} data-testid="convo-accept-btn" className="btn-primary inline-flex items-center gap-2 text-sm">
                                <Check size={14}/> Sí, me viene bien
                              </button>
                              <button onClick={() => rejectProposal(m.meta.slot)} disabled={loading} data-testid="convo-reject-btn" className="btn-ghost inline-flex items-center gap-2 text-sm">
                                <X size={14}/> No me viene bien
                              </button>
                            </div>
                          )}

                          {/* Ronda 33.1 · Final confirmation buttons — doble
                              confirmación obligatoria antes de escribir en
                              Google Calendar. */}
                          {isLast && m.role === "bot" && m.meta?.type === "final_confirmation" && convoStage === "awaiting_final_confirmation" && (
                            <div className="mt-3 flex flex-wrap gap-2 justify-start ml-1" data-testid="final-confirm-buttons">
                              <button onClick={() => finalizeBooking(m.meta.slot)} disabled={loading} data-testid="final-confirm-yes" className="btn-primary inline-flex items-center gap-2 text-sm">
                                <Check size={14}/> Sí, confírmalo
                              </button>
                              <button onClick={() => cancelFinalConfirmation()} disabled={loading} data-testid="final-confirm-no" className="btn-ghost inline-flex items-center gap-2 text-sm">
                                <X size={14}/> No, prefiero otro
                              </button>
                            </div>
                          )}

                          {/* Ronda 33.14 · Botón de Reintentar tras fallo
                              genérico de calendar (502, red, timeout). Mantiene
                              el slot elegido — la clienta no renegocia. */}
                          {isLast && m.role === "bot" && m.meta?.type === "booking_failed_retry" && convoStage === "booking_failed" && (
                            <div className="mt-3 flex flex-wrap gap-2 justify-start ml-1" data-testid="booking-retry-buttons">
                              <button
                                onClick={() => finalizeBooking(m.meta.slot)}
                                disabled={loading}
                                data-testid="booking-retry-btn"
                                className="btn-primary inline-flex items-center gap-2 text-sm"
                              >
                                <RefreshCcw size={14}/> Reintentar
                              </button>
                              <button
                                onClick={() => { setConvoStage("proposing"); pushBot("Vale, buscamos otro hueco. ¿Prefieres otro día, otra hora, o cambio de franja?"); }}
                                disabled={loading}
                                data-testid="booking-retry-cancel"
                                className="btn-ghost inline-flex items-center gap-2 text-sm"
                              >
                                <X size={14}/> Elegir otro hueco
                              </button>
                            </div>
                          )}

                          {isLast && m.role === "bot" && m.meta?.type === "escalate_offer" && convoStage === "escalate_offer" && (
                            <div className="mt-3 flex flex-wrap gap-2 justify-start ml-1" data-testid="escalate-buttons">
                              <button onClick={() => answerEscalation(true)} disabled={loading} data-testid="escalate-yes" className="btn-primary text-sm">Sí, pásaselo a Bea</button>
                              <button onClick={() => answerEscalation(false)} disabled={loading} data-testid="escalate-no" className="btn-ghost text-sm">No, seguimos aquí</button>
                            </div>
                          )}
                          {isLast && m.role === "bot" && m.meta?.type === "escalate_contact" && convoStage === "escalate_contact" && (
                            <div className="mt-3 flex flex-wrap gap-2 justify-start ml-1" data-testid="escalate-contact-buttons">
                              <button onClick={() => chooseContact("whatsapp")} disabled={loading} data-testid="escalate-contact-whatsapp" className="btn-primary text-sm">Por WhatsApp o teléfono</button>
                              <button onClick={() => chooseContact("email")} disabled={loading} data-testid="escalate-contact-email" className="btn-ghost text-sm">Por email</button>
                            </div>
                          )}

                          {/* Disambiguation buttons */}
                          {isLast && m.role === "bot" && m.meta?.type === "disambiguate" && convoStage === "disambiguate_shift" && (
                            <div className="mt-3 flex flex-wrap gap-2 justify-start ml-1" data-testid="disambiguate-buttons">
                              <button
                                onClick={() => resolveDisambiguation("time")}
                                disabled={loading}
                                data-testid="dis-time"
                                className="btn-primary text-sm"
                              >
                                Mismo día, otra hora
                              </button>
                              <button
                                onClick={() => resolveDisambiguation("date")}
                                disabled={loading}
                                data-testid="dis-date"
                                className="btn-ghost text-sm"
                              >
                                Fecha {m.meta.direction === "earlier" ? "anterior" : "posterior"}
                              </button>
                            </div>
                          )}

                          {/* Payment timing message → the payment card + upload
                              button are now rendered OUTSIDE the message loop
                              (see below) so they stay visible regardless of
                              which bubble is at the bottom (Bizum request,
                              text replies, etc.). */}
                          
                        </div>
                      );
                    })}
                    {loading && (
                      <div className="flex justify-start">
                        <div className="bg-[#FFF7E5] border border-[#B8912E]/25 px-4 py-2.5 rounded-2xl text-sm text-[#6C6355]">
                          Escribiendo…
                        </div>
                      </div>
                    )}
                    <div ref={chatEndRef}></div>
                  </div>

                  {/* Persistent payment card + upload button (always visible
                      while awaiting receipt). Sits BELOW the scrollable chat
                      so a request for Bizum, or any other message, cannot
                      hide it. */}
                  {convoStage === "await_receipt" && config?.payment_info && (
                    <div className="mt-5 border-t border-[#B8912E]/20 pt-5" data-testid="chat-payment-block">
                      <div className="rounded-2xl border border-[#B8912E]/40 bg-white p-5 text-sm space-y-2">
                        <div className="label-eyebrow">Datos de pago</div>
                        <div><span className="text-[#6C6355]">Titular:</span> <span className="text-[#141210]">{config.payment_info.account_holder}</span></div>
                        <div><span className="text-[#6C6355]">IBAN:</span> <span className="gold font-medium">{config.payment_info.iban}</span></div>
                        <div><span className="text-[#6C6355]">Importe:</span> <span className="text-[#141210]">{config.consultation_price_eur}€</span></div>
                        <div><span className="text-[#6C6355]">Concepto:</span> <span className="text-[#141210]">{config.payment_info.concept}</span></div>
                      </div>
                      {!clientEmail && (
                        <div className="mt-3">
                          <label className="text-xs text-[#6C6355] block mb-1">Email (opcional, para recibir la confirmación)</label>
                          <input
                            type="email"
                            data-testid="chat-email-input"
                            className="field text-sm"
                            placeholder="tucorreo@ejemplo.com"
                            value={clientEmail}
                            onChange={(e) => setClientEmail(e.target.value)}
                          />
                        </div>
                      )}
                      <label className="mt-4 block">
                        <div className="btn-primary inline-flex items-center gap-2 cursor-pointer text-sm w-full sm:w-auto justify-center" data-testid="chat-upload-btn">
                          <Upload size={14}/> {uploading ? "Subiendo..." : "Subir justificante aquí"}
                        </div>
                        <input
                          type="file"
                          accept="image/*,application/pdf"
                          className="hidden"
                          data-testid="chat-receipt-upload"
                          onChange={(e) => uploadReceiptInChat(e.target.files?.[0])}
                        />
                      </label>
                      <p className="text-[11px] text-[#8A8175] mt-2">
                        Puedes hacer una foto en el momento o subirla desde tu galería.
                      </p>
                    </div>
                  )}

                  {/* Ronda 31 P1: la clienta puede seguir preguntando
                      después de subir el justificante. Antes ocultábamos el
                      input en `receipt_sent`, lo cual bloqueaba la
                      conversación mientras Bea revisa el pago. */}
                  <div className="mt-5 border-t border-[#B8912E]/20 pt-4">
                      <div className="flex gap-2">
                        <input
                          data-testid="convo-text-input"
                          className="field text-sm"
                          placeholder={
                            convoStage === "ask_band"
                              ? "Ej: «mejor por la tarde» o «me da igual»"
                              : convoStage === "disambiguate_shift"
                              ? "Ej: «en el mismo día» o «otra fecha»"
                              : convoStage === "await_receipt"
                              ? "Escribe si tienes alguna duda…"
                              : convoStage === "receipt_sent"
                              ? "Puedes seguir escribiéndome…"
                              : convoStage === "exhausted"
                              ? "Escríbeme lo que quieras…"
                              : "Ej: «un poco antes», «no me va bien», «sí»"
                          }
                          value={chatText}
                          onChange={(e) => setChatText(e.target.value)}
                          onKeyDown={(e) => e.key === "Enter" && sendFreeText()}
                          disabled={loading}
                        />
                        <button onClick={sendFreeText} disabled={loading || !chatText.trim()} data-testid="convo-text-send" className="btn-primary px-4">
                          <Send size={14}/>
                        </button>
                      </div>
                      <p className="text-[11px] text-[#8A8175] mt-2">
                        Puedes seguir escribiéndome cuando quieras. Nunca se corta la conversación.
                      </p>
                    </div>

                  {convoStage === "has_prior_booking" && (
                    <div className="mt-4 flex flex-wrap gap-3 border-t border-[#B8912E]/20 pt-4">
                      <button
                        onClick={async () => {
                          // Refetch existing appointment for the same phone
                          setLoading(true);
                          try {
                            const { data } = await api.post("/booking/check-phone", { phone });
                            if (data.appointment) {
                              setAppointment(data.appointment);
                              setStep("existing");
                            } else {
                              // No appointment found (odd) — restart conversation
                              restartConversation();
                            }
                          } catch (err) {
                            toast.error("No pudimos comprobar tu cita. Prueba de nuevo.");
                          }
                          setLoading(false);
                        }}
                        data-testid="go-to-existing-btn"
                        className="btn-primary inline-flex items-center gap-2 text-sm"
                      >
                        <CalIcon size={14}/> Gestionar mi cita actual
                      </button>
                      <button onClick={restartConversation} data-testid="convo-restart-btn" className="btn-ghost text-sm">
                        Empezar de nuevo
                      </button>
                    </div>
                  )}

                  {convoStage === "exhausted" && (
                    <div className="mt-4 flex flex-wrap gap-3 border-t border-[#B8912E]/20 pt-4">
                      <button
                        onClick={() => setShowQR(true)}
                        data-testid="convo-whatsapp-btn"
                        className="btn-primary inline-flex items-center gap-2 text-sm"
                      >
                        <MessageCircle size={14}/> Escribir por WhatsApp
                      </button>
                      <button onClick={restartConversation} data-testid="convo-restart-btn" className="btn-ghost text-sm">
                        Empezar de nuevo
                      </button>
                    </div>
                  )}

                  {convoStage === "receipt_sent" && (
                    <div className="mt-6 border-t border-[#B8912E]/20 pt-5">
                      <p className="text-sm text-emerald-700 flex items-center gap-2"><Check size={14}/> Justificante recibido.</p>
                      <div className="mt-3 flex flex-wrap gap-3">
                        <Link to="/" className="btn-ghost text-sm" data-testid="convo-home">Volver al inicio</Link>
                        <button onClick={() => setShowQR(true)} data-testid="convo-whatsapp-post" className="btn-ghost text-sm inline-flex items-center gap-2">
                          <MessageCircle size={14}/> Escribir por WhatsApp
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}

              <UrgencyCard variant="booking"/>
            </motion.div>
          )}

          {step === "done" && (
            <motion.div key="done" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="card-gold text-center">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-[#B8912E]/12 border border-[#B8912E]/40 mx-auto">
                <Check className="gold" size={28}/>
              </div>
              <h2 className="font-serif text-3xl font-light mt-6 text-[#141210]">Justificante recibido</h2>
              <p className="text-[#3B342A] mt-4 leading-relaxed">
                Gracias. Vamos a revisar tu pago cuanto antes.
                <br/>Cuando confirmemos el ingreso, {clientEmail ? "recibirás un correo con la confirmación de tu cita" : "tu cita quedará confirmada y podrás verla al volver a entrar aquí con tu teléfono"}.
              </p>
              <p className="text-[#8A8175] text-xs mt-4">
                Tu hueco queda reservado el <span className="gold">{formatSpanishDate(booking.start)}</span> a las <span className="gold">{formatTime(booking.start)}</span> mientras revisamos.
              </p>
              <Link to="/" className="btn-ghost mt-8 inline-block" data-testid="done-home">Volver al inicio</Link>
            </motion.div>
          )}
        </AnimatePresence>

        {step === "phone" && !config?.vacation_mode && (
          <motion.div
            initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}
            className="card card-hover mt-8 cursor-pointer"
            data-testid="manage-appointment-block"
            role="button"
            onClick={() => {
              const el = document.querySelector('[data-testid="phone-input"]');
              if (el) {
                el.scrollIntoView({behavior:'smooth', block:'center'});
                setTimeout(() => el.focus(), 400);
              }
            }}
          >
            <div className="flex items-start gap-4">
              <div className="w-11 h-11 rounded-full bg-[#FFF7E5] border border-[#B8912E]/50 flex items-center justify-center shrink-0">
                <CalIcon className="gold" size={20}/>
              </div>
              <div>
                <div className="label-eyebrow">¿Ya tienes cita?</div>
                <h3 className="font-serif text-2xl md:text-3xl mt-2 text-[#0A0A0A]">Cambiar, cancelar o subir el justificante</h3>
                <p className="text-[#3B342A] text-base md:text-lg mt-3 leading-relaxed">
                  Pulsa aquí e introduce tu teléfono arriba para acceder a tu cita: cancelarla, cambiar horario o subir el comprobante de pago.
                </p>
              </div>
            </div>
          </motion.div>
        )}
      </main>
    </div>
  );
}

function ClientNoteCard({ phone, eventId }) {
  const [note, setNote] = useState("");
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const submit = async () => {
    const clean = note.trim();
    if (clean.length < 3 || !phone || !eventId) return;
    setSending(true);
    try {
      await api.post("/booking/note", { phone, event_id: eventId, note: clean });
      setSent(true);
      setNote("");
      setTimeout(() => setSent(false), 6000);
    } catch (e) {
      alert(e.response?.data?.detail || "No pude enviar tu nota. Inténtalo de nuevo en unos minutos.");
    } finally {
      setSending(false);
    }
  };
  return (
    <div className="card mt-6" data-testid="client-note-card">
      <div className="label-eyebrow">Nota para Bea</div>
      <h3 className="font-serif text-xl mt-2 text-[#141210]">¿Quieres dejarle algún mensaje?</h3>
      <p className="text-sm text-[#6C6355] mt-2 leading-relaxed">
        Por ejemplo: <em>«prefiero que me llames tú»</em>, o cualquier detalle que quieras que sepa antes de la cita. Le llegará por email al momento.
      </p>
      <textarea
        data-testid="client-note-input"
        className="field mt-4"
        rows={4}
        placeholder="Escribe aquí lo que quieras contarle…"
        value={note}
        onChange={(e) => setNote(e.target.value)}
        maxLength={2000}
      />
      <div className="mt-4 flex items-center gap-3 flex-wrap">
        <button
          onClick={submit}
          disabled={sending || note.trim().length < 3}
          data-testid="client-note-send"
          className="btn-primary inline-flex items-center gap-2 disabled:opacity-50"
        >
          {sending ? "Enviando…" : sent ? "Enviada ✓" : "Enviar nota"}
        </button>
        {sent && (
          <span className="text-sm text-emerald-700" data-testid="client-note-success">Bea ha recibido tu nota. Gracias.</span>
        )}
      </div>
    </div>
  );
}
